/** @format */

import {
	AttributeOption,
	selectDisabledAttributes,
	selectSelectedAttributeValue
} from '@pdp/attributes/data-access';
import {
	ChangeDetectorRef,
	Component,
	computed,
	input,
	OnChanges,
	OnDestroy,
	OnInit,
	output,
	SimpleChanges
} from '@angular/core';
import {
	HmfSelectableComponent,
	HmfSelectableOption,
	HmfSelectedOption,
	HmfSelectorComponent
} from '@dsg-tech/homefield-angular';
import {
	homefieldIconAlertInfoFilled,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { Subject, takeUntil } from 'rxjs';

import { AttributeLabelComponent } from '../attribute-label/attribute-label.component';
import { CommonModule } from '@angular/common';
import { FeatureConfigurationKeys } from '@shared/data-access/enums';
import { FeatureConfigurationService } from '@shared/data-access/services';
import { Store } from '@ngrx/store';

@Component({
	selector: 'pdp-attributes-components-default-attribute-selection',
	styleUrl: './selector-attributes.component.scss',
	templateUrl: './selector-attributes.component.html',
	imports: [CommonModule, AttributeLabelComponent, HmfSelectorComponent, HmfSelectableComponent],
	providers: [HomefieldIconsLibraryService]
})
export class SelectorAttributesComponent implements OnInit, OnChanges, OnDestroy {
	selectDisabledAttributes$ = this.store.select(selectDisabledAttributes);
	preselectionTitleMessage = "We've preselected your most recent size.";
	preselectionDescMessage =
		'Enjoy a personalized experience, saving you time and effort in selecting the right fit.';
	disabledAttributesState: { [key: string]: string[] } = {};
	attrSelectionState: { [key: string]: boolean } = {};
	attrUnavailableState: { [key: string]: boolean } = {};
	hmfSelectableOptions: HmfSelectableOption[] = [];

	private $UNSUBSCRIBE: Subject<void> = new Subject();

	readonly selectorAttributeData = input<AttributeOption[]>(undefined);
	readonly labelAttrGroupName = input<string>();
	readonly alternativeInStockIsShowing = input(false);
	readonly isConfigVariant = input(false);
	readonly overflowXScroll = input(true);
	readonly isAlternativeInStockEnabled = input(false);
	readonly selectedAttributeOnClick = output<AttributeOption>();

	private readonly selectAttributeAttrValue = computed(() =>
		this.store.selectSignal(selectSelectedAttributeValue(this.labelAttrGroupName()))()
	);

	constructor(
		private changeRef: ChangeDetectorRef,
		private readonly featureConfigurationService: FeatureConfigurationService,
		private store: Store,
		private homefieldIconsLibraryService: HomefieldIconsLibraryService
	) {
		this.homefieldIconsLibraryService.registerIcons([homefieldIconAlertInfoFilled]);
	}

	/**
	 * @description
	 * Function to preselect a pdp size
	 *
	 * @param size size to be preselected
	 * @param title string title to show on preselection message
	 * @param description string description to show on preselection message
	 * @param hideMessage boolean to hide the preselection message
	 */
	public preselectBySize = ({
		description,
		hideMessage,
		size,
		title
	}: {
		description?: string;
		hideMessage?: boolean;
		size: string;
		title?: string;
	}) => {
		const attributeData = this.selectorAttributeData().find((data) => data.value === size);
		if (attributeData) {
			this.selectedAttribute(attributeData);

			if (title) {
				this.preselectionTitleMessage = title;
			}

			if (description) {
				this.preselectionDescMessage = description;
			}

			this.showPreselectionMessage = hideMessage !== true;
			this.changeRef.detectChanges();
		}
	};
	showPreselectionMessage = false;

	ngOnInit() {
		this.featureConfigurationService.onFeature(
			FeatureConfigurationKeys.PRESELECT_PDP_BY_SIZE,
			this.preselectBySize
		);
		this.selectDisabledAttributes$.pipe(takeUntil(this.$UNSUBSCRIBE)).subscribe((value) => {
			this.disabledAttributesState = value;
			this.updateAttributeSelectionState();
			this.updateAttributeUnavailableState();
			this.updateHmfSelectableOptions();
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (
			changes['selectorAttributeData'] ||
			changes['labelAttrGroupName'] ||
			changes['alternativeInStockIsShowing']
		) {
			this.updateAttributeSelectionState();
			this.updateAttributeUnavailableState();
			this.updateHmfSelectableOptions();
		}
	}

	ngOnDestroy() {
		this.$UNSUBSCRIBE.next();
		this.$UNSUBSCRIBE.complete();
	}

	handleHmfSelectableChange(options: HmfSelectedOption[]) {
		const optionValue = String(options[0]?.value);
		const selectedAttribute = this.selectorAttributeData()?.find(
			(attr: AttributeOption) => attr.value === optionValue
		);
		if (selectedAttribute) {
			this.selectedAttribute(selectedAttribute);
		}
	}

	/**
	 * @description Emits an event when a selector (i.e size, height etc.. non color attr) is clicked
	 * and updates the current selector to be selected.
	 **/
	selectedAttribute(selectedAttributeValue: AttributeOption) {
		this.selectedAttributeOnClick.emit(selectedAttributeValue);
		this.updateAttributeSelectionState();
		this.updateAttributeUnavailableState();
		this.updateHmfSelectableOptions();
	}

	updateAttributeSelectionState(): void {
		this.attrSelectionState = {};
		const selectorAttributeData = this.selectorAttributeData();
		if (selectorAttributeData) {
			for (const attributeOption of selectorAttributeData) {
				if (attributeOption?.value) {
					this.attrSelectionState[attributeOption.value] =
						this.checkForIsSelected(attributeOption);
				}
			}
		}
		this.changeRef.detectChanges();
	}

	updateAttributeUnavailableState(): void {
		this.attrUnavailableState = {};
		const selectorAttributeData = this.selectorAttributeData();
		if (selectorAttributeData) {
			for (const attributeOption of selectorAttributeData) {
				if (attributeOption?.value) {
					this.attrUnavailableState[attributeOption.value] =
						this.checkForIsUnavailable(attributeOption);
				}
			}
		}
		this.changeRef.detectChanges();
	}

	updateHmfSelectableOptions() {
		this.hmfSelectableOptions =
			this.selectorAttributeData()?.map((attr, index) => ({
				id: index,
				value: attr.value,
				unavailable: this.attrUnavailableState[attr.value],
				selected: this.attrSelectionState[attr.value],
				classList: 'hmf-mx-xs'
			})) ?? [];
	}

	checkForIsSelected(attributesOption: AttributeOption): boolean {
		return (
			this.selectAttributeAttrValue()?.toLowerCase() === attributesOption.value.toLowerCase()
		);
	}

	checkForIsUnavailable(attributesOption: AttributeOption): boolean {
		return Boolean(
			!this.disabledAttributesState?.[attributesOption.name]?.includes(
				attributesOption.value
			) ||
				(this.alternativeInStockIsShowing() &&
					this.attrSelectionState?.[attributesOption.value])
		);
	}

	protected trackByFn(index: number, item: AttributeOption): string {
		return index.toString() + item.value;
	}
}
